import type { CalculatorResult } from "./calculatorData";

function validatePortfolioValues(peak: number, current: number) {
  if (!Number.isFinite(peak) || peak <= 0 || !Number.isFinite(current) || current < 0) {
    throw new RangeError("Peak must be positive and current value must be non-negative, both finite.");
  }
}

/** Two-point drawdown, not maximum drawdown or a forecast. Null means no finite recovery. */
export function portfolioDrawdown(peak: number, current: number) {
  validatePortfolioValues(peak, current);
  const loss = Math.max(0, peak - current);
  const recoveryPercent = current === 0 ? null : loss / current * 100;
  if (recoveryPercent !== null && !Number.isFinite(recoveryPercent)) {
    throw new RangeError("Recovery gain exceeds numeric precision limits.");
  }
  return { loss, drawdownPercent: loss / peak * 100, recoveryPercent };
}

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const percent = (value: number) => `${value.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;

export function calculatePortfolioDrawdown(values: Record<string, number>): CalculatorResult {
  validatePortfolioValues(values.peak, values.current);
  // Match currency precision and enforce the calculator's one-cent peak minimum.
  // Raw validation above still rejects negative amounts before they can round to zero.
  const peak = Math.max(0.01, Math.round(values.peak * 100) / 100);
  const current = Math.round(values.current * 100) / 100;
  const { loss, drawdownPercent, recoveryPercent } = portfolioDrawdown(peak, current);
  const recovery = recoveryPercent === null ? "No finite gain" : percent(recoveryPercent);
  return {
    headlineLabel: "Decline from entered peak",
    headlineValue: percent(drawdownPercent),
    headlineDetail: loss === 0 ? "At or above the entered peak. No recovery needed." : `Gain needed to regain peak: ${recovery}.`,
    headlineTone: loss > 0 ? "negative" : "neutral",
    stats: [
      { label: "Loss from peak", value: money.format(loss) },
      { label: "Gain needed to recover", value: recovery, detail: recoveryPercent === null ? "A percentage gain on zero cannot restore the balance." : "Measured against the current value, not the peak." },
      { label: "Entered peak", value: money.format(peak) },
      { label: "Current value", value: money.format(current) },
    ],
    chart: { kind: "breakdown", valueLabel: "Entered peak: remaining value and loss", segments: [
      { label: "Remaining portion of peak", value: Math.min(current, peak), tone: "accent" },
      { label: "Loss from peak", value: loss, tone: "negative" },
    ] },
    note: current > peak ? "The current value is above the entered peak. Use the new high as the peak for subsequent drawdown measurements" : "Use comparable values with no deposits or withdrawals between them. This measures current drawdown, not maximum drawdown, and does not predict whether or when recovery will happen",
  };
}
